import React from 'react';
import { Box, Button, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import { Bot, DecisionValue, Snapshot } from '../lib/types';
import {
  NO_REC_FILTERS, REC_SORT_KEYS, RecFilters, RecRow, RecSortKey, filterRecs, recsToCsv, sortRecs,
} from '../lib/botPage';
import { fmtIct } from '../lib/ict';
import { PriorityChip, ProvLine, VerdictChip } from '../chips';
import { DecisionStateChip } from '../chips';
import { DecisionControl } from '../DecisionControl';
import { InstructComposer } from '../InstructComposer';
import { OVERLINE, RADIUS, TABLE_HEAD_SX, TONE } from '../lib/brand';

const HEADERS: { key: RecSortKey | null; label: string }[] = [
  { key: 'date', label: 'Date' }, { key: 'priority', label: 'Priority' }, { key: 'finding', label: 'Finding' }, { key: null, label: 'Recommended action' },
  { key: 'verdict', label: 'C-suite verdict' }, { key: 'decision', label: 'Grant decision' }, { key: 'implementation', label: 'Implementation' }, { key: 'source', label: 'Source' }, { key: null, label: 'Why?' },
];

const IMPL_LABEL: Record<string, string> = { not_started: 'Not started', queued: 'Queued', in_progress: 'In progress', done: 'Done', failed: 'Failed', blocked: 'Blocked' };
const clip = (s?: string | null, n = 140) => (s ? (s.length > n ? `${s.slice(0, n)}…` : s) : 'Not stated in source');

export function RecommendationsTab({
  bot, snapshot, rows, expandedId, onExpand, onOpenInstruction,
}: {
  bot: Bot; snapshot: Snapshot; rows: RecRow[]; expandedId: string | null; onExpand: (id: string | null) => void; onOpenInstruction: (id: string) => void;
}) {
  const [filters, setFilters] = React.useState<RecFilters>(NO_REC_FILTERS);
  const [sort, setSort] = React.useState<{ key: RecSortKey; dir: 'asc' | 'desc' }>({ key: 'priority', dir: 'asc' });
  const shown = React.useMemo(() => sortRecs(filterRecs(rows, filters), sort.key, sort.dir), [rows, filters, sort]);
  const set = <K extends keyof RecFilters>(k: K, v: RecFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(filters) !== JSON.stringify(NO_REC_FILTERS);

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([recsToCsv(shown)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bot.slug}-recommendations.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // A recommendation named in the URL scrolls into view once.
  React.useEffect(() => {
    if (!expandedId) return;
    const el = document.getElementById(`rec-${expandedId}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [expandedId]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.2 }}>
        <TextField size="small" label="Search" value={filters.q} onChange={(e) => set('q', e.target.value)} sx={{ minWidth: 200 }} />
        <TextField select size="small" label="Verdict" value={filters.verdict} onChange={(e) => set('verdict', e.target.value as RecFilters['verdict'])} sx={{ minWidth: 130 }}>
          {['all', 'agree', 'alternative', 'none'].map((v) => <MenuItem key={v} value={v}>{v === 'all' ? 'All verdicts' : v === 'none' ? 'Unreviewed' : v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Grant decision" value={filters.decision} onChange={(e) => set('decision', e.target.value as RecFilters['decision'])} sx={{ minWidth: 140 }}>
          {['all', 'pending', 'yes', 'no', 'instructed'].map((v) => <MenuItem key={v} value={v}>{v === 'all' ? 'All decisions' : v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Priority" value={filters.priority} onChange={(e) => set('priority', e.target.value as RecFilters['priority'])} sx={{ minWidth: 120 }}>
          {['all', 'P0', 'P1', 'P2', 'none'].map((v) => <MenuItem key={v} value={v}>{v === 'all' ? 'All priorities' : v === 'none' ? 'Unscored' : v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Implementation" value={filters.implementation} onChange={(e) => set('implementation', e.target.value as RecFilters['implementation'])} sx={{ minWidth: 150 }}>
          {['all', 'not_started', 'queued', 'in_progress', 'done', 'failed', 'blocked'].map((v) => <MenuItem key={v} value={v}>{v === 'all' ? 'All statuses' : IMPL_LABEL[v]}</MenuItem>)}
        </TextField>
        {dirty && <Button size="small" onClick={() => setFilters(NO_REC_FILTERS)}>Clear filters</Button>}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={exportCsv} disabled={shown.length === 0}>Export CSV ({shown.length})</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.8 }} data-testid="rec-count">{shown.length} of {rows.length} recommendations</Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="no-recs">No dated recommendation files found for this bot (checked recommendations/{bot.slug}/).</Typography>
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing matches these filters.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Recommendations" sx={{ minWidth: 980 }}>
            <TableHead sx={TABLE_HEAD_SX}>
              <TableRow>
                {HEADERS.map((h) => (
                  <TableCell key={h.label} sortDirection={h.key && sort.key === h.key ? sort.dir : false} aria-sort={h.key && sort.key === h.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                    {h.key && REC_SORT_KEYS.includes(h.key) ? (
                      <TableSortLabel active={sort.key === h.key} direction={sort.key === h.key ? sort.dir : 'asc'} onClick={() => setSort((s) => (s.key === h.key ? { key: h.key as RecSortKey, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: h.key as RecSortKey, dir: 'asc' }))}>
                        {h.label}
                      </TableSortLabel>
                    ) : h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => {
                const open = expandedId === r.rec.id;
                const rec = r.rec;
                return (
                  <React.Fragment key={rec.id}>
                    <TableRow
                      id={`rec-${rec.id}`}
                      hover
                      tabIndex={0}
                      onClick={() => onExpand(open ? null : rec.id)}
                      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onExpand(open ? null : rec.id); } }}
                      sx={{ cursor: 'pointer', '& td': { verticalAlign: 'top' } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{rec.date || 'no data'}</TableCell>
                      <TableCell><PriorityChip priority={rec.priority_code || 'Unscored'} score={rec.score} bucket={rec.bucket} /></TableCell>
                      <TableCell sx={{ maxWidth: 260, fontSize: 13, fontWeight: 600 }}>{clip(rec.title, 120)}</TableCell>
                      <TableCell sx={{ maxWidth: 260, fontSize: 12.5 }}>{clip(rec.recommended_action)}</TableCell>
                      <TableCell>{r.verdict === 'none' || !rec.verdict ? <Typography variant="caption" color="text.secondary">Unreviewed</Typography> : <VerdictChip exec={rec.verdict.exec || 'Exec'} verdict={r.verdict} />}</TableCell>
                      <TableCell>{r.decision === 'pending' ? <Typography variant="caption" color="text.secondary">Pending</Typography> : <DecisionStateChip value={r.decision as DecisionValue} />}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{IMPL_LABEL[r.impl] || r.impl}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}><ProvLine prov={rec.source} fmt={fmtIct} /></TableCell>
                      <TableCell>
                        <Button size="small" aria-expanded={open} aria-label={`Why? ${clip(rec.title, 60)}`} onClick={(e) => { e.stopPropagation(); onExpand(open ? null : rec.id); }} sx={{ textTransform: 'none', minWidth: 0 }}>Why?</Button>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow data-testid={`rec-detail-${rec.id}`}>
                        <TableCell colSpan={HEADERS.length} sx={{ bgcolor: 'action.hover' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, p: 0.5 }}>
                            <Box>
                              <Typography variant="overline" sx={{ ...OVERLINE }}>Finding</Typography>
                              <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{rec.finding || 'Not stated in source'}</Typography>
                              <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 1 }}>Recommended action ({rec.recommended_by === 'exec' ? 'exec' : 'bot'})</Typography>
                              <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{rec.recommended_action || 'Not stated in source'}</Typography>
                              {rec.verdict && rec.verdict.status !== 'none' && (
                                <Box sx={{ mt: 1, border: '1px solid', borderColor: rec.verdict.status === 'alternative' ? TONE.warn.border : TONE.good.border, borderRadius: RADIUS.lg, p: 1 }}>
                                  <VerdictChip exec={rec.verdict.exec || 'Exec'} verdict={rec.verdict.status} />
                                  <Typography sx={{ fontSize: 12.5, mt: 0.6 }}>{rec.verdict.text || 'Not stated in source'}</Typography>
                                  {rec.verdict.alternative_action && <Typography sx={{ fontSize: 12.5, mt: 0.6 }}><b>Alternative:</b> {rec.verdict.alternative_action}</Typography>}
                                  <ProvLine prov={rec.verdict.prov} fmt={fmtIct} />
                                </Box>
                              )}
                              {rec.why && rec.why.excerpt && (
                                <>
                                  <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 1 }}>Why this is here</Typography>
                                  <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.md, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', maxHeight: 220, overflow: 'auto' }}>{rec.why.excerpt}</Box>
                                </>
                              )}
                              {rec.why && rec.why.excerpt_status && rec.why.excerpt_status !== 'ok' && !rec.why.excerpt && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>Source excerpt withheld ({rec.why.excerpt_status}).</Typography>
                              )}
                            </Box>
                            <Box>
                              <Typography variant="overline" sx={{ ...OVERLINE }}>Your decision</Typography>
                              {r.action && r.action.decidable ? (
                                <DecisionControl row={{ action: r.action, bot }} snapshot={snapshot} onOpenInstruction={onOpenInstruction} layout="drawer" />
                              ) : (
                                <>
                                  <Typography variant="body2" color="text.secondary" data-testid="historical-note" sx={{ mb: 1 }}>
                                    {r.action ? 'This item has no Yes / No decision.' : 'This is an earlier recommendation and is no longer in the current Action Center, so it cannot be approved here. You can still ask a question.'}
                                  </Typography>
                                  <InstructComposer label={`Ask ${bot.name} about this recommendation`} kind="question" relatedActionId={r.action ? r.action.id : null} targets={[{ slug: bot.slug, label: bot.name }]} placeholder={`Ask ${bot.short || bot.name} about “${clip(rec.title, 50)}”…`} submitLabel="Ask" />
                                </>
                              )}
                              <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 1.5 }}>History ({rec.versions || (rec.history || []).length || 1} version{(rec.versions || 1) === 1 ? '' : 's'})</Typography>
                              <Typography variant="caption" color="text.secondary" component="div">First seen {rec.first_seen || 'no data'} · last seen {rec.last_seen || 'no data'}</Typography>
                              {(rec.history || []).map((h, i) => <ProvLine key={`${h.path}-${i}`} prov={h} fmt={fmtIct} />)}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
